// packages/web-ui/src/hooks/useComposer.ts
//
// Story W2-S7 — `useComposer` host hook. Owns:
//   1. textarea value + caret (controlled).
//   2. auto-grow algorithm (40 px → 200 px linear, scroll past 200 px).
//   3. chip list (resolved + ambiguous).
//   4. attachment list (uploadId + contentType from W2-S6).
//   5. derived `hasAmbiguousMention` flag.
//   6. send-disabled logic (`isSendDisabled`).
//
// The hook does NOT touch the chat slice — sends go through the
// `onSend(body, mentions, attachments)` callback supplied by the parent
// (`<ChatPanel>`). This keeps the composer testable in isolation and
// honours the dispatch-anti-interference rule (other agents own the chat
// slice).

import type { RefObject } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { KbEntityKind, MentionCandidate } from '@tessa-lean/base/api-schemas/index.js';
import {
  AMBIGUOUS_ENTITY_ID,
  buildMentionsFromChips,
  chipPlaceholder,
  serialiseValueForSend,
  type ComposerAttachment,
  type ComposerChip,
} from '../state/composer.js';

/** Auto-grow bounds (px). UX §6.1.6 line 586. */
export const COMPOSER_MIN_HEIGHT = 40;
export const COMPOSER_MAX_HEIGHT = 200;

export interface UseComposerOptions {
  initialValue?: string;
  /** Optional id factory for chips (testing). */
  generateChipId?: () => string;
}

export interface ComposerSendPayload {
  body: string;
  mentions: { chipText: string; entityId: string }[];
  attachments: ComposerAttachment[];
}

export interface UseComposerResult {
  // textarea state
  value: string;
  caret: number;
  setValue: (next: string, caret?: number) => void;
  setCaret: (caret: number) => void;
  // ref for the host textarea
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  // auto-grow handler
  applyAutoGrow: (textarea: HTMLTextAreaElement) => void;

  // chips
  chips: ComposerChip[];
  insertChip: (
    candidate: MentionCandidate,
    triggerOffset: number,
    triggerLength: number,
  ) => void;
  insertAmbiguousChip: (chipText: string, triggerOffset: number, triggerLength: number) => void;
  removeChip: (chipId: string) => void;
  hasAmbiguousMention: boolean;

  // attachments
  attachments: ComposerAttachment[];
  addAttachment: (attachment: ComposerAttachment) => void;
  removeAttachment: (uploadId: string) => void;

  // derived
  isEmpty: boolean;

  // build send payload + clear
  buildSendPayload: () => ComposerSendPayload | null;
  clear: () => void;
}

let _idCounter = 0;
function defaultGenerateId(): string {
  _idCounter += 1;
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `composer-chip-${_idCounter}`;
}

export function useComposer(options: UseComposerOptions = {}): UseComposerResult {
  const genId = options.generateChipId ?? defaultGenerateId;

  const [value, _setValue] = useState<string>(options.initialValue ?? '');
  const [caret, _setCaret] = useState<number>((options.initialValue ?? '').length);
  const [chips, setChips] = useState<ComposerChip[]>([]);
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([]);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Derived: ambiguous flag.
  const hasAmbiguousMention = useMemo(
    () => chips.some((c) => c.entityId === AMBIGUOUS_ENTITY_ID),
    [chips],
  );

  const setValue = useCallback((next: string, nextCaret?: number): void => {
    _setValue(next);
    if (nextCaret !== undefined) {
      _setCaret(nextCaret);
    }
    // GC chips whose placeholder no longer appears in `next`.
    setChips((prev) => prev.filter((chip) => next.includes(chipPlaceholder(chip.chipText))));
  }, []);

  const setCaret = useCallback((next: number): void => {
    _setCaret(next);
  }, []);

  const insertChip = useCallback(
    (candidate: MentionCandidate, triggerOffset: number, triggerLength: number): void => {
      _setValue((prev) => {
        const placeholder = chipPlaceholder(candidate.chipText);
        const before = prev.slice(0, triggerOffset);
        const after = prev.slice(triggerOffset + triggerLength);
        const next = `${before}${placeholder}${after}`;
        _setCaret((before + placeholder).length);
        return next;
      });
      setChips((prev) => [
        ...prev,
        {
          id: genId(),
          entityId: candidate.entityId,
          kind: candidate.kind,
          chipText: candidate.chipText,
          qualifiedName: candidate.qualifiedName,
          insertOffset: triggerOffset,
        },
      ]);
    },
    [genId],
  );

  const insertAmbiguousChip = useCallback(
    (chipText: string, triggerOffset: number, triggerLength: number): void => {
      _setValue((prev) => {
        const placeholder = chipPlaceholder(chipText);
        const before = prev.slice(0, triggerOffset);
        const after = prev.slice(triggerOffset + triggerLength);
        const next = `${before}${placeholder}${after}`;
        _setCaret((before + placeholder).length);
        return next;
      });
      setChips((prev) => [
        ...prev,
        {
          id: genId(),
          entityId: AMBIGUOUS_ENTITY_ID,
          kind: 'part' as KbEntityKind,
          chipText,
          qualifiedName: chipText,
          insertOffset: triggerOffset,
        },
      ]);
    },
    [genId],
  );

  const removeChip = useCallback((chipId: string): void => {
    setChips((prev) => {
      const target = prev.find((c) => c.id === chipId);
      if (target === undefined) return prev;
      const placeholder = chipPlaceholder(target.chipText);
      _setValue((prevValue) => {
        const idx = prevValue.indexOf(placeholder);
        if (idx < 0) return prevValue;
        return prevValue.slice(0, idx) + prevValue.slice(idx + placeholder.length);
      });
      return prev.filter((c) => c.id !== chipId);
    });
  }, []);

  const addAttachment = useCallback((attachment: ComposerAttachment): void => {
    setAttachments((prev) => [...prev, attachment]);
  }, []);

  const removeAttachment = useCallback((uploadId: string): void => {
    setAttachments((prev) => prev.filter((a) => a.uploadId !== uploadId));
  }, []);

  // Auto-grow: reset to auto, read scrollHeight, clamp to [40, 200].
  const applyAutoGrow = useCallback((textarea: HTMLTextAreaElement): void => {
    textarea.style.height = 'auto';
    const scrollHeight = textarea.scrollHeight;
    const clamped = Math.min(
      Math.max(scrollHeight, COMPOSER_MIN_HEIGHT),
      COMPOSER_MAX_HEIGHT,
    );
    textarea.style.height = `${clamped}px`;
    if (scrollHeight > COMPOSER_MAX_HEIGHT) {
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.overflowY = 'hidden';
    }
  }, []);

  const isEmpty = useMemo(() => value.trim().length === 0, [value]);

  const buildSendPayload = useCallback((): ComposerSendPayload | null => {
    if (isEmpty) return null;
    const body = serialiseValueForSend(value);
    const mentions = buildMentionsFromChips(chips);
    return {
      body,
      mentions,
      attachments: [...attachments],
    };
  }, [value, chips, attachments, isEmpty]);

  const clear = useCallback((): void => {
    _setValue('');
    _setCaret(0);
    setChips([]);
    setAttachments([]);
  }, []);

  return {
    value,
    caret,
    setValue,
    setCaret,
    textareaRef,
    applyAutoGrow,
    chips,
    insertChip,
    insertAmbiguousChip,
    removeChip,
    hasAmbiguousMention,
    attachments,
    addAttachment,
    removeAttachment,
    isEmpty,
    buildSendPayload,
    clear,
  };
}
